/**
 * The company API envelope (ADR-0007 in the platform repo). Every response has the same shape; only this module
 * and `http.ts` look inside it — generated hooks and the UI get the data itself.
 */

export interface ApiFieldError {
  /** Wire name of the field, e.g. `o_contact_name`. */
  field: string;
  code: string;
  message: string;
}

export interface EnvelopeError {
  /** HTTP status. */
  code: number;
  /** Stable id the UI branches on: `VALIDATION_ERROR`, `checkout.cart_stale`, ... */
  sub_code: string;
  /** Already in the request language. */
  message: string;
  details: unknown;
}

export interface Envelope {
  success: boolean;
  response_body: { response_type: "object" | "array" | "null"; response_data: unknown } | null;
  error: EnvelopeError | null;
  links?: unknown;
  meta?: unknown;
}

export function isEnvelope(body: unknown): body is Envelope {
  return typeof body === "object" && body !== null && "success" in body && "response_body" in body;
}

/** The data of a successful envelope: paginated lists keep `meta` and `links` next to the items. */
export function unwrap(envelope: Envelope): unknown {
  const data = envelope.response_body?.response_data ?? null;
  const paginated =
    envelope.response_body?.response_type === "array" &&
    envelope.links !== undefined &&
    typeof envelope.meta === "object" &&
    envelope.meta !== null &&
    "current_page" in envelope.meta;
  if (paginated) {
    return { items: data, meta: envelope.meta, links: envelope.links };
  }
  return data;
}
